import { Fragment, useCallback,useEffect } from "react";
import { Dialog, DialogTitle, Typography, useTheme } from "@mui/material";

import messages from "../../../../constants/messages";
import { useScimWizardContext } from "../../../../providers/ScimWizardProvider";
import CoAppDestructiveConfirmationModal from "../../../global/components/modals/CoAppDestructiveConfirmationModal";
import { CoAppActionButton, CoAppCancelTextButton, CoAppLightTooltip } from "../../../global/styled/global.styled";

import ScimWizardStepper from "./stepper/ScimWizardStepper";
import Step1 from "./steps/Step1";
import Step2 from "./steps/Step2";
import Step3 from "./steps/Step3";
import Step4 from "./steps/Step4";
import Step5 from "./steps/Step5";
import Step6 from "./steps/Step6";
import Step7 from "./steps/Step7";
import { ScimWizardButtonContainer } from "./styled/scim-wizard.styled";
import RoleValidationModal from "./RoleValidationModal";
import ScimWizardGroupDetectionModal from "./ScimWizardGroupDetectionModal";

export default function ScimWizard() {
	const {
		alertUser,
		activeStep,
		deleteConfiguration,
		identityProviderName,
		initOrganization,
		setExternalGroupsTryAgain,
		getContinueButtonText,
		noRolesMappedOpen,
		superAdminNotMapped,
		roleMappingStatus,
		roleValidationLoading,
		externalGroupsDialogOpen,
		setExternalGroupsDialogOpen,
		externalGroupsFound,
		externalGroupsTryAgain,
		handleSubmit,
		handleBack,
		handleCancelExit,
		handleExternalGroupRetrieval,
		handleNext,
		handleCloseRoleValidationModal,
		handleClearCurrentConfiguration,
		blocker
	} = useScimWizardContext();

	const theme = useTheme();

	const getStepContent = (step) => {
		switch (step) {
			case 0:
				return <Step1 />;
			case 1:
				return <Step2 />;
			case 2:
				return <Step3 />;
			case 3:
				return <Step4 />;
			case 4:
				return <Step5 />;
			case 5:
				return <Step6 />;
			case 6:
				return <Step7 />;
			default:
				return "Unknown step";
		}
	};

	// declare the handler once so its identity is stable
	const handleUnload = useCallback((e) => {
		console.log("handleUnload in ScimWizard");
		deleteConfiguration(e);
	}, []);
  
	useEffect(() => {
		initOrganization();
		window.addEventListener("beforeunload", alertUser);
		window.addEventListener("unload", handleUnload);
  
		return () => {
			window.removeEventListener("beforeunload", alertUser);
			window.removeEventListener("unload", handleUnload);
		};
	}, [handleUnload]);

	return (
		<Fragment>
			<ScimWizardStepper />
			{getStepContent(activeStep)}
			<CoAppDestructiveConfirmationModal
				dialogOpen={blocker.state === "blocked"}
				dialogTitle="Leave SSO + SCIM Configuration?"
				dialogMessage="You have not completed SSO + SCIM configuration. All progress will be lost."
				confirmClickHandler={handleClearCurrentConfiguration}
				cancelClickHandler={handleCancelExit}
				actionText="Confirm"
			/>

			<ScimWizardGroupDetectionModal
				dialogOpen={externalGroupsDialogOpen}
				closeClickHandler={() => setExternalGroupsDialogOpen(false)}
				confirmClickHandler={handleNext}
				tryAgainClickHandler={handleExternalGroupRetrieval}
				identityProviderName={identityProviderName}
				groupsDetected={externalGroupsFound}
				tryAgain={externalGroupsTryAgain}
				setTryAgain={setExternalGroupsTryAgain}
			/>
			<RoleValidationModal
				dialogOpen={noRolesMappedOpen}
				loading={roleValidationLoading}
				roleItems={[]}
				confirmClickHandler={handleNext}
				cancelClickHandler={handleCloseRoleValidationModal}
				roleMappingStatus={roleMappingStatus}
				identityProviderName={identityProviderName}
			/>
			<Dialog
				open={roleValidationLoading}
				slotProps={{
					paper: {
						sx: {
							borderRadius: "10px"
						}
					}
				}}					
			>
				<DialogTitle>
					<Typography variant="body1" sx={{ fontWeight: 600, marginLeft: "10px" }}>Validating your configured role mappings...</Typography>
				</DialogTitle>
			</Dialog>
			<ScimWizardButtonContainer>
				<CoAppCancelTextButton onClick={handleBack} disabled={activeStep === 0}>Back</CoAppCancelTextButton>
				{
					activeStep === 4 && superAdminNotMapped ?
						<CoAppLightTooltip title={messages.SCIM_ROLE_WARNING_TOOLTIP}>
							<span>
								<CoAppActionButton
									onClick={handleSubmit}
									disabled={true}
									sx={{
										"&.Mui-disabled": {
											backgroundColor: theme.palette.action.disabledBackground,
											color: theme.palette.action.disabled
										}
									}}
								>{getContinueButtonText()}</CoAppActionButton>
							</span>
						</CoAppLightTooltip>
						:
						<CoAppActionButton onClick={handleSubmit}>{getContinueButtonText()}</CoAppActionButton>
				}
			</ScimWizardButtonContainer>
		</Fragment>
	);
}
